import { Component } from '@theme/component';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const SWIPE_THRESHOLD = 40;

/**
 * Full-bleed hero carousel with autoplay, progress indicators and swipe.
 *
 * Progressive enhancement: without JS the first slide is visible and readable,
 * the controls only become useful once the component upgrades.
 *
 * @extends {Component<{ slide: HTMLElement[], dot: HTMLButtonElement[], toggle?: HTMLButtonElement }>}
 */
class SanyHeroCarousel extends Component {
  requiredRefs = ['slide'];

  /** @type {number} */
  #index = 0;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #timer = null;
  /** @type {boolean} */
  #paused = false;
  /** @type {number | null} */
  #pointerStartX = null;

  /** Autoplay is wasted work on a hidden tab, and it desyncs the progress bar. */
  #onVisibilityChange = () => {
    if (document.hidden) this.#stop();
    else if (!this.#paused) this.#restart();
  };

  get #interval() {
    return Number(this.dataset.interval ?? 6000);
  }

  get #autoplayWanted() {
    return this.dataset.autoplay === 'true' && this.refs.slide.length > 1 && !matchMedia(REDUCED_MOTION).matches;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#show(0);
    this.#restart();

    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#stop();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  /** @param {number} index */
  #show(index) {
    const { slide, dot } = this.refs;
    const count = slide.length;
    this.#index = ((index % count) + count) % count;

    slide.forEach((element, i) => {
      const active = i === this.#index;
      element.classList.toggle('sany-hero__slide--active', active);
      element.setAttribute('aria-hidden', String(!active));
      // Keep off-screen slides out of the tab order.
      element.querySelectorAll('a, button').forEach((node) => {
        if (node instanceof HTMLElement) node.tabIndex = active ? 0 : -1;
      });
    });

    dot?.forEach((button, i) => {
      const active = i === this.#index;
      button.classList.toggle('sany-hero__dot--active', active);
      button.setAttribute('aria-current', String(active));
    });

    this.#syncVideo();
  }

  /**
   * Plays the active slide's video from the top and pauses every other one.
   * Off-screen video is wasted battery, and autoplay never fires on its own
   * inside a slide that is still hidden.
   */
  #syncVideo() {
    this.refs.slide.forEach((element, i) => {
      const video = element.querySelector('video');
      if (!video) return;

      if (i !== this.#index) {
        video.pause();
        return;
      }

      // preload="none" means there is nothing buffered yet, so ask for data.
      if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) video.load();

      video.currentTime = 0;
      this.#playWhenVisible(video);
    });
  }

  /**
   * A browser refuses to play video inside a hidden element, and the slide is
   * still mid-fade for 600ms after the class changes. Retry across that window
   * rather than waiting on `canplay`, which never fires again for a clip that
   * is already buffered.
   *
   * @param {HTMLVideoElement} video
   */
  #playWhenVisible(video) {
    const delays = [0, 120, 350, 700, 1100];
    let index = 0;

    const attempt = () => {
      // The slide may have moved on while we were waiting.
      if (this.refs.slide[this.#index]?.contains(video) !== true) return;

      video
        .play()
        .catch(() => {
          index += 1;
          if (index < delays.length) setTimeout(attempt, delays[index]);
        });
    };

    setTimeout(attempt, delays[0]);
  }

  #restart() {
    this.#stop();
    if (!this.#autoplayWanted || this.#paused) return;

    this.#timer = setTimeout(() => {
      this.#show(this.#index + 1);
      this.#restart();
    }, this.#interval);

    this.#animateProgress();
  }

  #stop() {
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = null;
  }

  /** Restarts the fill animation on the active indicator so it tracks the timer. */
  #animateProgress() {
    const active = this.refs.dot?.[this.#index]?.querySelector('.sany-hero__dot-fill');
    if (!(active instanceof HTMLElement)) return;

    active.style.animation = 'none';
    // Force a reflow so the animation restarts from zero.
    void active.offsetWidth;
    active.style.animation = '';
  }

  /** @param {number} index */
  #goTo(index) {
    this.#show(index);
    this.#restart();
  }

  /** @param {Event} event */
  onDotClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.sany-hero__dot');
    const index = this.refs.dot?.indexOf(/** @type {HTMLButtonElement} */ (button)) ?? -1;

    if (index !== -1) this.#goTo(index);
  }

  /** @param {KeyboardEvent} event */
  onDotKeydown(event) {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;

    event.preventDefault();
    this.#goTo(this.#index + step);
    this.refs.dot?.[this.#index]?.focus();
  }

  onToggleClick() {
    this.#paused = !this.#paused;
    this.classList.toggle('sany-hero--paused', this.#paused);

    const toggle = this.refs.toggle;
    if (toggle) {
      toggle.setAttribute('aria-pressed', String(this.#paused));
      toggle.setAttribute('aria-label', this.#paused ? toggle.dataset.labelPlay ?? '' : toggle.dataset.labelPause ?? '');
    }

    if (this.#paused) this.#stop();
    else this.#restart();
  }

  /** @param {PointerEvent} event */
  onPointerDown(event) {
    this.#pointerStartX = event.clientX;
  }

  /** @param {PointerEvent} event */
  onPointerUp(event) {
    if (this.#pointerStartX === null) return;

    const delta = event.clientX - this.#pointerStartX;
    this.#pointerStartX = null;

    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    this.#goTo(this.#index + (delta < 0 ? 1 : -1));
  }
}

if (!customElements.get('sany-hero-carousel')) {
  customElements.define('sany-hero-carousel', SanyHeroCarousel);
}
